import { useEffect } from "react";

/**
 * Scroll a focused field into the visual viewport (above the on-screen keyboard).
 */
export function scrollFieldAboveKeyboard(el) {
  if (!el || typeof el.scrollIntoView !== "function") return;
  const run = () => {
    try {
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    } catch (_) {
      el.scrollIntoView();
    }
  };
  run();
  if (typeof window === "undefined") return;
  window.setTimeout(run, 120);
  window.setTimeout(run, 320);
}

const KEYBOARD_OPEN_THRESHOLD_PX = 80;
/** Samsung 3-button / gesture bar often covers the page while safe-area reports 0. */
const ANDROID_NAV_CLEARANCE_PX = 72;
/**
 * Samsung's keyboard feature bar (S24 and similar) is drawn on top of the
 * visual viewport, so Chrome still reports that strip as visible page.
 * One toolbar row plus a few pixels so the field border is not flush with it.
 */
export const ANDROID_KEYBOARD_FEATURE_BAR_PX = 64;

export function keyboardFeatureBarPx({
  keyboardOpen = false,
  userAgent = "",
  coarsePointer = false,
} = {}) {
  if (!keyboardOpen || !coarsePointer) return 0;
  if (!/Android|SamsungBrowser/i.test(userAgent)) return 0;
  return ANDROID_KEYBOARD_FEATURE_BAR_PX;
}

/**
 * Visible window for a login field. Coordinates match getBoundingClientRect
 * (visual viewport). The feature bar is cut off the bottom, then intersected
 * with the form scroller so a page that is already shortened is not inset twice.
 */
export function loginFieldView(shellRect, viewHeight, featureBarPx = 0) {
  const margin = 10;
  const vvBottom = Math.max(0, viewHeight - Math.max(0, featureBarPx));
  const top = Math.max(0, shellRect?.top ?? 0);
  const bottom = Math.min(vvBottom, shellRect?.bottom ?? vvBottom);
  return {
    top: top + margin,
    bottom: Math.max(top + margin + 1, bottom - margin),
  };
}

/**
 * Scroll delta that keeps the focused field inside `view`.
 * Positive scrolls content upward. The label comes along when it fits.
 */
export function fieldVisibilityDelta(block, view) {
  const fieldTop = block.fieldTop;
  const fieldBottom = block.fieldBottom;
  const viewTop = view.top;
  const viewBottom = view.bottom;
  if (!(viewBottom > viewTop)) return 0;
  if (![fieldTop, fieldBottom, viewTop, viewBottom].every(Number.isFinite)) return 0;

  if (fieldBottom > viewBottom) {
    if (fieldBottom - fieldTop > viewBottom - viewTop) return fieldTop - viewTop;
    return fieldBottom - viewBottom;
  }
  if (fieldTop < viewTop) return fieldTop - viewTop;
  if (block.top < viewTop) {
    const room = viewBottom - fieldBottom;
    if (room <= 0) return 0;
    return Math.max(block.top - viewTop, -room);
  }
  return 0;
}

function readCssPx(name) {
  if (typeof document === "undefined") return 0;
  const n = parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name));
  return Number.isFinite(n) ? n : 0;
}

/** Keep a login input, including its label, above the keyboard and its feature bar. */
export function pinFocusedFieldAboveKeyboard(el) {
  if (!el || typeof el.getBoundingClientRect !== "function") return;
  const wrap = el.closest?.(".input") || el;
  const label = wrap.previousElementSibling?.classList?.contains("label")
    ? wrap.previousElementSibling
    : null;
  const shell = el.closest?.(".login-page-shell");
  const vv = window.visualViewport;
  const viewHeight = vv?.height || window.innerHeight || 0;
  const view = loginFieldView(
    shell?.getBoundingClientRect?.() || null,
    viewHeight,
    readCssPx("--login-kb-bar")
  );
  const wrapRect = wrap.getBoundingClientRect();
  const delta = fieldVisibilityDelta(
    {
      top: label ? label.getBoundingClientRect().top : wrapRect.top,
      bottom: wrapRect.bottom,
      fieldTop: wrapRect.top,
      fieldBottom: wrapRect.bottom,
    },
    view
  );
  if (Math.abs(delta) < 1) return;
  if (shell) shell.scrollTop += delta;
}

let safeAreaProbe = null;

function readSafeAreaBottom() {
  if (typeof document === "undefined") return 0;
  if (!safeAreaProbe) {
    safeAreaProbe = document.createElement("div");
    safeAreaProbe.setAttribute("aria-hidden", "true");
    safeAreaProbe.style.cssText =
      "position:fixed;left:0;bottom:0;width:0;height:0;visibility:hidden;pointer-events:none;padding-bottom:env(safe-area-inset-bottom,0px);";
    document.body.appendChild(safeAreaProbe);
  }
  return parseFloat(window.getComputedStyle(safeAreaProbe).paddingBottom) || 0;
}

function phoneBottomClearance() {
  const reported = readSafeAreaBottom();
  const vv = window.visualViewport;
  const layoutH = window.innerHeight || 0;
  const vvGap = vv ? Math.max(0, Math.round(layoutH - ((vv.offsetTop || 0) + vv.height))) : 0;
  const shortSide = Math.min(window.screen?.width || 0, window.screen?.height || 9999);
  const phone = window.matchMedia("(pointer: coarse)").matches && shortSide <= 520;
  const android = /Android|SamsungBrowser/i.test(navigator.userAgent || "");
  const androidFloor = phone && android && reported < 32 ? ANDROID_NAV_CLEARANCE_PX : 0;
  return Math.max(reported, vvGap, androidFloor);
}

/**
 * Hide the on-screen keyboard. The readonly flip is what actually dismisses it
 * on iOS when blur() alone leaves the keyboard up.
 */
export function dismissVirtualKeyboard(el) {
  if (!el || typeof el.blur !== "function") return;
  const wasReadOnly = Boolean(el.readOnly);
  el.readOnly = true;
  el.blur();
  const active = typeof document !== "undefined" ? document.activeElement : null;
  if (active && active !== el && active !== document.body && typeof active.blur === "function") {
    if (active.closest?.(".digiping-chat__composer")) active.blur();
  }
  if (!wasReadOnly) {
    window.setTimeout(() => {
      el.readOnly = false;
    }, 80);
  }
}

/**
 * While a Pingflo composer is focused, keep its shell inside the visual viewport
 * so the text field sits just above the on-screen keyboard.
 * scrollIntoView is intentionally not used: on mobile it pans the page and
 * hides the field above the keyboard.
 */
export function pinComposerAboveKeyboard(el) {
  if (!el || typeof window === "undefined") return () => {};
  const shell = el.closest(".digiping-chat__shell");
  if (!shell) return () => {};

  const apply = () => {
    const vv = window.visualViewport;
    if (!vv) return;
    const layoutH = window.innerHeight || 0;
    const top = Math.round(vv.offsetTop || 0);
    const height = Math.round(vv.height || layoutH);
    const keyboardOpen = layoutH > 0 && layoutH - height > KEYBOARD_OPEN_THRESHOLD_PX;
    if (!keyboardOpen || height <= 0) {
      shell.style.top = "";
      shell.style.height = "";
      shell.style.maxHeight = "";
      shell.style.minHeight = "";
      return;
    }
    shell.style.top = `${top}px`;
    shell.style.height = `${height}px`;
    shell.style.maxHeight = `${height}px`;
    shell.style.minHeight = "0px";
  };

  apply();
  const timers = [40, 120, 280, 480].map((ms) => window.setTimeout(apply, ms));
  const vv = window.visualViewport;
  vv?.addEventListener("resize", apply);
  vv?.addEventListener("scroll", apply);
  window.addEventListener("orientationchange", apply);

  return () => {
    timers.forEach((id) => window.clearTimeout(id));
    vv?.removeEventListener("resize", apply);
    vv?.removeEventListener("scroll", apply);
    window.removeEventListener("orientationchange", apply);
    shell.style.top = "";
    shell.style.height = "";
    shell.style.maxHeight = "";
    shell.style.minHeight = "";
  };
}

/**
 * Keep chrome inside the visual viewport (mobile keyboard + browser chrome).
 * When the keyboard is open, zero bottom safe-area padding so input trays sit flush.
 */
function isEditableField(el) {
  return el instanceof HTMLElement && el.matches("input, textarea, select, [contenteditable='true']");
}

export function useVisualViewportHeight({
  enabled = true,
  heightVar = "--wizard-vvh",
  topVar = "--wizard-vv-top",
  safeBottomVar = "--wizard-safe-bottom",
  keyboardClass = "wizard-keyboard-open",
  scrollFocusedFields = true,
  pinFocusedField = false,
  keyboardBarVar = null,
} = {}) {
  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;
    const root = document.documentElement;
    let baseline = 0;
    let pinTimers = [];
    const clearPinTimers = () => {
      pinTimers.forEach((id) => window.clearTimeout(id));
      pinTimers = [];
    };
    const rememberBaseline = () => {
      if (isEditableField(document.activeElement)) return;
      const vv = window.visualViewport;
      baseline = Math.max(window.innerHeight || 0, vv?.height || 0);
    };
    const pinActiveField = () => {
      if (!pinFocusedField) return;
      const el = document.activeElement;
      if (!isEditableField(el) || !el.closest(".login-page")) return;
      clearPinTimers();
      const run = () => {
        if (!el.isConnected) return;
        pinFocusedFieldAboveKeyboard(el);
      };
      run();
      pinTimers = [40, 120, 280, 480, 760].map((ms) => window.setTimeout(run, ms));
    };
    const apply = () => {
      const vv = window.visualViewport;
      const layoutH = window.innerHeight || 0;
      const h = vv?.height ?? layoutH;
      const top = vv?.offsetTop ?? 0;
      const gapOpen = layoutH > 0 && layoutH - h > KEYBOARD_OPEN_THRESHOLD_PX;
      const focused = isEditableField(document.activeElement);
      if (!focused) baseline = Math.max(layoutH, h);
      else if (!baseline) baseline = Math.max(layoutH, h);
      const keyboardOpen =
        gapOpen || (pinFocusedField && focused && baseline - h > KEYBOARD_OPEN_THRESHOLD_PX);
      root.style.setProperty(heightVar, `${Math.round(h)}px`);
      root.style.setProperty(topVar, `${Math.round(top)}px`);
      const clearance = phoneBottomClearance();
      root.style.setProperty(
        safeBottomVar,
        keyboardOpen ? "0px" : `${clearance}px`
      );
      if (keyboardBarVar) {
        const coarse = window.matchMedia?.("(pointer: coarse)")?.matches;
        const bar = keyboardFeatureBarPx({
          keyboardOpen,
          userAgent: navigator.userAgent || "",
          coarsePointer: Boolean(coarse),
        });
        root.style.setProperty(keyboardBarVar, `${bar}px`);
      }
      root.classList.toggle(keyboardClass, keyboardOpen);
      document.body.classList.toggle(keyboardClass, keyboardOpen);
      if (keyboardOpen) pinActiveField();
    };
    apply();
    const vv = window.visualViewport;
    vv?.addEventListener("resize", apply);
    vv?.addEventListener("scroll", apply);
    window.addEventListener("resize", apply);
    const onFocusIn = (event) => {
      const el = event.target;
      if (!isEditableField(el)) return;
      apply();
      if (pinFocusedField && el.closest(".login-page")) {
        window.requestAnimationFrame(() => pinFocusedFieldAboveKeyboard(el));
        return;
      }
      if (scrollFocusedFields) scrollFieldAboveKeyboard(el);
    };
    const onFocusOut = () => {
      window.setTimeout(apply, 80);
      window.setTimeout(apply, 280);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    if (pinFocusedField) {
      document.addEventListener("pointerdown", rememberBaseline, { passive: true });
      document.addEventListener("touchstart", rememberBaseline, { passive: true });
    }
    const onOrientation = () => {
      baseline = 0;
      apply();
    };
    window.addEventListener("orientationchange", onOrientation);
    return () => {
      clearPinTimers();
      vv?.removeEventListener("resize", apply);
      vv?.removeEventListener("scroll", apply);
      window.removeEventListener("orientationchange", onOrientation);
      window.removeEventListener("resize", apply);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("pointerdown", rememberBaseline);
      document.removeEventListener("touchstart", rememberBaseline);
      root.style.removeProperty(heightVar);
      root.style.removeProperty(topVar);
      root.style.removeProperty(safeBottomVar);
      if (keyboardBarVar) root.style.removeProperty(keyboardBarVar);
      root.classList.remove(keyboardClass);
      document.body.classList.remove(keyboardClass);
    };
  }, [
    enabled,
    heightVar,
    topVar,
    safeBottomVar,
    keyboardClass,
    scrollFocusedFields,
    pinFocusedField,
    keyboardBarVar,
  ]);
}

/**
 * Keep Digiping wizard chrome inside the visual viewport (browser zoom + mobile chrome).
 */
export function useWizardViewportHeight() {
  useVisualViewportHeight({ enabled: true });
}
